import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Package, CreditCard, Sparkles, Users, HelpCircle } from "lucide-react";

const FAQ_CATEGORIES = [
  {
    id: "ordering",
    label: "Ordering & Shipping",
    icon: Package,
    color: "#D4AF37",
    questions: [
      {
        q: "How long does shipping take?",
        a: "UK orders typically arrive within 2-5 business days. Standard Alchemist cymbals are made-to-order with a 10-14 day lead time. Items marked 'Ready to Ship' in our Live Shop dispatch within 24-48 hours. International shipping varies by destination — usually 5-14 business days."
      },
      {
        q: "Do you ship internationally?",
        a: "Yes! We ship worldwide to most countries. International shipping costs are calculated at checkout based on your location. For large orders or trade inquiries outside the UK, please contact us for a custom quote."
      },
      {
        q: "Can I track my order?",
        a: "Absolutely. Once your order ships, you'll receive an email with tracking information. You can also view your order status anytime by logging into your account and visiting the 'Orders' section, or use our Order Tracking page."
      },
      {
        q: "What are the shipping costs?",
        a: "UK shipping starts from £5.00. EU shipping from £12.00, and Rest of World from £18.00. Free UK shipping is available on orders over £150. Exact costs are shown at checkout before payment."
      },
      {
        q: "Can I change or cancel my order?",
        a: "If your order hasn't shipped yet, contact us immediately at contact@templeofsounddistro.com and we'll do our best to accommodate changes. Once an order is dispatched, we cannot cancel it, but you can return items under our returns policy."
      }
    ]
  },
  {
    id: "products",
    label: "Products & Custom Orders",
    icon: HelpCircle,
    color: "#22C55E",
    questions: [
      {
        q: "Are Alchemist cymbals handmade?",
        a: "Yes, every Alchemist cymbal is handcrafted in Turkey by skilled artisans using traditional techniques passed down through generations. Each cymbal is unique, with subtle variations in tone and appearance that give it character."
      },
      {
        q: "What cymbal series do you offer?",
        a: "We stock a wide range of Alchemist series including: Origin (including Brilliant, Aged, Duo, and OriginX variants), Mystic & Mystic Vortex, Eclipse & Eclipse Custom, Earth & Earth Halo, Opulent & OpulentX, Spectrum & Spectrum Dry, Helios, and Ignition. Each series has its own tonal character — visit our shop to explore."
      },
      {
        q: "Can I request a custom cymbal?",
        a: "Absolutely! We offer custom cymbal services through our B2B quoting system. Whether you need specific sizes, finishes, or tonal characteristics, contact us to discuss your requirements. Custom orders typically have a 4-6 week lead time."
      },
      {
        q: "How do I care for my cymbals?",
        a: "Wipe your cymbals with a dry, soft cloth after playing to remove fingerprints and moisture. Avoid harsh chemicals or abrasive cleaners. For deeper cleaning, use a cymbal-specific cleaner. Store cymbals in padded bags to prevent keyholing and edge damage."
      },
      {
        q: "What wood types are available for drumsticks?",
        a: "London Drumstick Co offers American Hickory (balanced weight, durability) and Maple (lightweight, fast response). Each wood type affects feel, balance, and sound — explore our Custom Builder to find your perfect match."
      }
    ]
  },
  {
    id: "payments",
    label: "Payments & Returns",
    icon: CreditCard,
    color: "#3B82F6",
    questions: [
      {
        q: "What payment methods do you accept?",
        a: "We accept all major credit and debit cards (Visa, Mastercard, American Express) through our secure Stripe checkout. Apple Pay and Google Pay are also available. For B2B orders, we can arrange bank transfers or invoice payments."
      },
      {
        q: "Can I use gift cards?",
        a: "Yes! Temple of Sound gift cards can be redeemed at checkout. Simply enter your gift card code in the designated field, and the balance will be applied to your order. Gift cards are valid for 12 months from purchase."
      },
      {
        q: "What's your return policy?",
        a: "We offer a 14-day return policy for unused items in original condition. Cymbals must be unplayed and in original packaging. Custom orders and personalized items cannot be returned unless faulty. Contact us to initiate a return."
      },
      {
        q: "How do refunds work?",
        a: "Once we receive and inspect your return, refunds are processed within 5-7 business days to your original payment method. Shipping costs are non-refundable unless the return is due to our error or a faulty product."
      },
      {
        q: "Is my payment information secure?",
        a: "Absolutely. All payments are processed through Stripe, a PCI-DSS Level 1 certified payment provider. We never store your full card details on our servers. Your financial information is encrypted and protected."
      }
    ]
  },
  {
    id: "membership",
    label: "Membership & Loyalty",
    icon: Sparkles,
    color: "#D4AF37",
    questions: [
      {
        q: "What are Gold Dust points?",
        a: "Gold Dust is our loyalty currency for Alchemist Cymbals. Earn 1 point per £1 spent on cymbals. Redeem points for discounts on future purchases — 100 Gold Dust = £1 off. Points never expire as long as your account remains active."
      },
      {
        q: "What are Beat Tokens?",
        a: "Beat Tokens are earned on London Drumstick Co purchases — 1 token per £1 spent. Like Gold Dust, redeem them for discounts at checkout. Both loyalty currencies reward your passion for quality percussion gear."
      },
      {
        q: "How do membership tiers work?",
        a: "We offer three tiers: Free (15% member discount), Pro (20% discount, 2x loyalty points, free UK shipping over £50), and VIP (25% discount, 3x loyalty points, always free UK shipping). Pro is £4.99/month with a free 7-day trial. VIP is £9.99/month. Upgrade anytime from Account > Membership."
      },
      {
        q: "What benefits do Pro members get?",
        a: "Pro members enjoy: 20% off all orders, 2x loyalty points on every purchase, free UK shipping on orders over £50, 24-hour early access to new products, and exclusive Pro-only promotions. Cancel anytime with no penalties."
      },
      {
        q: "What do VIP members get?",
        a: "VIP is our top tier: 25% off all orders, 3x loyalty points, always free UK shipping (no minimum), 48-hour early access to new releases, priority customer support, and access to exclusive VIP-only products. VIP is £9.99/month."
      },
      {
        q: "Do loyalty points expire?",
        a: "No! Gold Dust and Beat Tokens never expire as long as you make at least one purchase per year. Your points are safe and waiting for whenever you're ready to redeem them."
      },
      {
        q: "Can I refer friends?",
        a: "Yes! Share your unique referral link from your Account page. When a friend makes their first purchase, you earn 50 bonus Gold Dust and they get 25 Gold Dust as a welcome gift. There's no limit to how many friends you can refer."
      }
    ]
  },
  {
    id: "b2b",
    label: "B2B & Trade",
    icon: Users,
    color: "#8B5CF6",
    questions: [
      {
        q: "Do you offer dealer/trade pricing?",
        a: "Yes, we have competitive trade pricing for verified music retailers, drum shops, and distributors. Register for a B2B account to access wholesale pricing, bulk order discounts, and dedicated account management."
      },
      {
        q: "How do I become a stockist?",
        a: "We're always looking for quality retail partners. Apply through our B2B portal or contact us at contact@templeofsounddistro.com with your business details. We'll review your application and get back to you within 48 hours."
      },
      {
        q: "Can I request a quote for bulk orders?",
        a: "Absolutely. Use our B2B Quote Builder to create detailed quotes for large orders. Our team will review and provide competitive pricing based on volume. Quotes are valid for 30 days and can be converted directly to orders."
      },
      {
        q: "Do you offer dropshipping?",
        a: "We offer dropshipping partnerships for established retailers with verified track records. Products ship in neutral packaging with your branding options available. Contact us to discuss dropshipping arrangements."
      },
      {
        q: "What about artist endorsements?",
        a: "Our Artist Program is separate from membership tiers and is invitation-only for professional drummers. Artists receive special pricing, free products for review, social media features, and collaborative opportunities. Interested? Apply through our Contact page with your portfolio."
      }
    ]
  }
];

export default function FAQSection() {
  const [activeCategory, setActiveCategory] = useState("ordering");
  const [openQuestions, setOpenQuestions] = useState({});

  const toggleQuestion = (categoryId, questionIndex) => {
    const key = `${categoryId}-${questionIndex}`;
    setOpenQuestions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const currentCategory = FAQ_CATEGORIES.find(c => c.id === activeCategory);

  return (
    <section id="faq" className="py-20 px-4 bg-gradient-to-b from-[#050505] to-[#0a0a0a]">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-3">
            Got Questions?
          </p>
          <h2 
            className="text-4xl md:text-5xl text-white mb-4"
            style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.05em" }}
          >
            FREQUENTLY ASKED QUESTIONS
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto">
            Everything you need to know about our products, ordering, membership, and more.
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {FAQ_CATEGORIES.map((category) => {
            const Icon = category.icon;
            const isActive = activeCategory === category.id;
            return (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all ${
                  isActive
                    ? "bg-[#D4AF37] text-black font-medium"
                    : "bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white border border-white/10"
                }`}
                data-testid={`faq-tab-${category.id}`}
              >
                <Icon className="h-4 w-4" style={{ color: isActive ? "inherit" : category.color }} />
                {category.label}
              </button>
            );
          })}
        </div>

        {/* Questions */}
        <motion.div
          key={activeCategory}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="space-y-3"
        >
          {currentCategory?.questions.map((item, index) => {
            const isOpen = openQuestions[`${activeCategory}-${index}`];
            return (
              <div
                key={index}
                className={`bg-[#0f0f0f] border rounded-sm overflow-hidden transition-colors ${
                  isOpen ? "border-[#D4AF37]/50" : "border-white/10 hover:border-white/20"
                }`}
              >
                <button
                  onClick={() => toggleQuestion(activeCategory, index)}
                  className="w-full flex items-center justify-between p-5 text-left"
                  data-testid={`faq-q-${activeCategory}-${index}`}
                >
                  <span className={`font-medium pr-4 ${isOpen ? "text-[#D4AF37]" : "text-white"}`}>
                    {item.q}
                  </span>
                  <ChevronDown 
                    className={`h-5 w-5 flex-shrink-0 transition-transform ${
                      isOpen ? "rotate-180 text-[#D4AF37]" : "text-zinc-500"
                    }`} 
                  />
                </button>
                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="px-5 pb-5 pt-0">
                        <p className="text-zinc-400 leading-relaxed">{item.a}</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </motion.div>

        {/* Contact CTA */}
        <div className="mt-12 text-center">
          <p className="text-zinc-500 mb-4">Still have questions?</p>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white/5 border border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-sm transition-colors"
            data-testid="faq-contact-link"
          >
            Contact Us
            <ChevronDown className="h-4 w-4 rotate-[-90deg]" />
          </a>
        </div>
      </div>
    </section>
  );
}
